import api from '@/api';

/**
 * DeMaxtore customer data via shared `api` instance: every request sends
 * `Authorization: Bearer <token>` from `localStorage` key `access_token` (see `src/api.js`).
 *
 * Paths (relative to `REACT_APP_BACKEND_URL` + `/api`):
 * - `GET /account/me` — session (AuthContext)
 * - `GET /dashboard/customer-home` — dashboard summary (not `/customer/dashboard`; nginx-safe)
 * - `GET /dashboard/customer/rfqs` — RFQ list (not `/rfqs`; nginx often 404s `/api/rfqs` on legacy stacks)
 * - `POST /public/catalog-rfq` — server-to-server only: demaxtore.com catalog form → same Mongo (env `CATALOG_RFQ_INGEST_TOKEN`, header `X-DeMaxtore-Catalog-RFQ-Token`)
 */

/** @returns {Promise<import('axios').AxiosResponse>} */
export function getDashboard() {
  return api.get('/dashboard/customer-home');
}

/** @returns {Promise<import('axios').AxiosResponse>} */
export function getRfqs() {
  return api.get('/dashboard/customer/rfqs');
}

/** Body matches backend `RFQCreate` (title required); optional: request_details, delivery_location, supplier_type, company_name, contact_name, phone, quantity, unit, … @returns {Promise<import('axios').AxiosResponse>} */
export function createCustomerRfq(body) {
  return api.post('/dashboard/customer/rfqs', body);
}

/**
 * Normalizes dashboard payload (handles common API shape variants).
 * Purchase orders are loaded from the portal API, not from DeMaxtore here.
 * @param {unknown} payload — response.data from getDashboard
 */
export function normalizeDashboard(payload) {
  let d = payload && typeof payload === 'object' ? payload : {};
  if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
    d = d.data;
  }
  const rfqs = Array.isArray(d.rfqs) ? d.rfqs : [];
  const pendingQuotes =
    d.pendingQuotes ?? d.pending_quotes ?? d.pendingQuotesCount ?? 0;
  return {
    rfqs,
    pendingQuotes: typeof pendingQuotes === 'number' ? pendingQuotes : Number(pendingQuotes) || 0,
  };
}

/**
 * @param {unknown} payload — response.data from getRfqs
 * @returns {unknown[]}
 */
export function normalizeRfqsList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.rfqs)) return payload.rfqs;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  const inner = payload.data;
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner.rfqs)) return inner.rfqs;
    if (Array.isArray(inner.items)) return inner.items;
  }
  return [];
}
